import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AutoAwesome,
  Sensors,
  Rule,
  DescriptionOutlined
} from "@mui/icons-material";
import GraphCanvas from "modules/knowledge-library/pages/knowledge-library/components/graph-canvas/GraphCanvas";
import {
  GraphEdge,
  GraphNode
} from "modules/knowledge-library/data/models/knowledge-graph.model";
import { PidAnalysisService } from "modules/processing-hub/data/services/pid-analysis.service";
import { mapKnowledgeGraphResponse } from "modules/processing-hub/data/utils/knowledge-graph.util";
import "./DrawingIntelligence.scss";

const FALLBACK_SUMMARY =
  "AI Drawing Summary The uploaded engineering drawings have been " +
  "successfully processed and analyzed using AI. Engineering symbols, " +
  "equipment tags, pipelines, instruments, and document metadata have been " +
  "extracted and validated against the Engineering Information Management " +
  "(EIM) repository. The analysis identified multiple engineering changes, " +
  "revision differences, and affected assets that require engineering " +
  "review. These findings will be used by downstream workflows such as " +
  "Engineering Document Intelligence and Management of Change (MOC), if " +
  "applicable.";

const CONNECTED_ENTITIES = [
  {
    icon: <Sensors />,
    tone: "amber",
    title: "Pressure Sensor 04",
    meta: "EQUIPMENT • HAS_COMPONENT"
  },
  {
    icon: <Rule />,
    tone: "red",
    title: "Min output 3000 PSI",
    meta: "REQUIREMENT • HAS_REQUIREMENT"
  },
  {
    icon: <DescriptionOutlined />,
    tone: "blue",
    title: "ENG_HB_2023_V2",
    meta: "DOCUMENT • SOURCED_FROM"
  }
];

const DrawingIntelligence: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const documentGuidCode = id;

  const [graphNodes, setGraphNodes] = useState<GraphNode[]>([]);
  const [graphEdges, setGraphEdges] = useState<GraphEdge[]>([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [isLoadingGraph, setIsLoadingGraph] = useState(false);
  const [graphError, setGraphError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string>("");

  useEffect(() => {
    let active = true;
    const loadGraph = async () => {
      if (!documentGuidCode) return;
      setIsLoadingGraph(true);
      setGraphError(null);
      try {
        const result = await PidAnalysisService.getKnowledgeGraph(
          documentGuidCode
        );
        if (!active) return;
        if (result.hasErrors) {
          setGraphError(
            result.errors.join(", ") || "Failed to load knowledge graph."
          );
          setGraphNodes([]);
          setGraphEdges([]);
          return;
        }
        if (result.value) {
          const mapped = mapKnowledgeGraphResponse(result.value);
          setGraphNodes(mapped.nodes);
          setGraphEdges(mapped.edges);
          setSummary(result.value.summary || FALLBACK_SUMMARY);
        }
      } catch {
        if (active) setGraphError("Failed to load knowledge graph.");
      } finally {
        if (active) setIsLoadingGraph(false);
      }
    };
    loadGraph();
    return () => {
      active = false;
    };
  }, [documentGuidCode]);

  return (
    <div className="drawing-intelligence">
      <button className="di-back" onClick={() => navigate(-1)}>
        &lt; Back
      </button>
      <h1 className="di-title">Drawing Intelligence</h1>

      {/* AI Summary */}
      <div className="di-summary-card">
        <div className="di-summary-head">
          <AutoAwesome className="di-summary-icon" />
          <h2>AI Summary</h2>
        </div>
        <p className="di-summary-text">
          {isLoadingGraph ? "Loading summary..." : summary || FALLBACK_SUMMARY}
        </p>
      </div>

      {/* Graph + Properties */}
      <div className="di-content">
        <div className="di-graph-card">
          {isLoadingGraph ? (
            <div className="di-graph-state">Loading knowledge graph...</div>
          ) : graphError ? (
            <div className="di-graph-state di-graph-error">{graphError}</div>
          ) : graphNodes.length === 0 ? (
            <div className="di-graph-state">
              No knowledge graph available for this drawing.
            </div>
          ) : (
            <GraphCanvas
              nodes={graphNodes}
              edges={graphEdges}
              selectedNodeId={selectedNodeId}
              onNodeSelect={setSelectedNodeId}
            />
          )}
        </div>

        <aside className="di-sidebar">
          <div className="di-panel">
            <h3 className="di-panel-title">PROPERTIES</h3>
            <div className="di-prop-row">
              <span className="di-prop-label">Discipline</span>
              <span className="di-prop-value">Mechanical</span>
            </div>
            <div className="di-prop-row">
              <span className="di-prop-label">Status</span>
              <span className="di-prop-value di-status-verified">
                <span className="di-status-dot" /> Verified
              </span>
            </div>
            <div className="di-prop-row">
              <span className="di-prop-label">Criticality</span>
              <span className="di-badge-high">HIGH</span>
            </div>
            <div className="di-prop-row">
              <span className="di-prop-label">Last Updated</span>
              <span className="di-prop-value">2023-10-12</span>
            </div>
          </div>

          <div className="di-panel">
            <h3 className="di-panel-title">CONNECTED ENTITIES (8)</h3>
            <div className="di-entities">
              {CONNECTED_ENTITIES.map((entity) => (
                <div key={entity.title} className="di-entity">
                  <span className={`di-entity-icon di-entity-${entity.tone}`}>
                    {entity.icon}
                  </span>
                  <div className="di-entity-text">
                    <span className="di-entity-title">{entity.title}</span>
                    <span className="di-entity-meta">{entity.meta}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default DrawingIntelligence;
